import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { Modal } from '../../components/Modal';
import { downloadCSV } from '../../utils/csv';
import { formatDateTimeInBusinessTZ } from '../../utils/date';
import { Download, ArrowUpDown, Search, Plus, Minus } from 'lucide-react';
import { ACH_STALE_DAYS, achAgeDays, countStaleAch, summarizeCashActivity } from '../utils/cashActivity';
import { useUrlTabState } from '../../hooks/useUrlTabState';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ParentResult {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
}

interface GrantResult {
  new_total_balance_cents: number;
  new_withdrawable_balance_cents: number;
  new_non_withdrawable_balance_cents: number;
}

interface GrantForm {
  amount: string;
  bucket: 'non_withdrawable' | 'withdrawable';
  note: string;
}

interface BalanceSummaryRow {
  account_id: string;
  parent_id: string;
  is_active: boolean;
  created_at: string;
  total_cents: number;
  withdrawable_cents: number;
  non_withdrawable_cents: number;
  last_activity_at: string | null;
  parent_first_name: string | null;
  parent_last_name: string | null;
  parent_email: string;
}

interface BalanceTransaction {
  id: string;
  account_id: string;
  type: 'credit' | 'debit' | 'refund' | 'withdrawal';
  bucket: 'withdrawable' | 'non_withdrawable';
  amount_cents: number;
  source_ref_type: string | null;
  source_ref_id: string | null;
  related_tx_id: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
}

interface WithdrawalForm {
  amount: string;
  method: 'zelle' | 'cash';
  note: string;
}

// Pending/failed ACH Add Funds purchases that have not yet credited the wallet.
// Funds only post once Stripe settles the ACH (payment_intent.succeeded); until
// then the row sits in balance_ach_pending_purchases as 'processing'.
interface AchPendingRow {
  id: string;
  amount_cents: number;
  status: 'processing' | 'failed';
  created_at: string;
}

type SortField = 'family' | 'total' | 'withdrawable' | 'non_withdrawable' | 'last_activity';
type SortDir = 'asc' | 'desc';

// A balance_transactions row for the cross-parent activity feed. The per-parent
// history drawer answers "what happened to this family"; this answers "what is
// happening to BHCS Cash overall", which nothing surfaced before.
interface ActivityRow {
  id: string;
  account_id: string;
  type: BalanceTransaction['type'];
  bucket: BalanceTransaction['bucket'];
  amount_cents: number;
  source_ref_type: string | null;
  note: string | null;
  created_at: string;
}

// An ACH top-up that has left the parent's bank but has not settled. These are
// invisible on the per-parent table because the wallet is not credited yet, and
// ACH settlement runs 4-5 days behind the purchase.
interface AchInFlightRow {
  id: string;
  parent_id: string;
  amount_cents: number;
  created_at: string;
}

type ActivityWindow = '7d' | '30d' | '90d' | 'all';

type SubTab = 'activity' | 'parents';
const BALANCES_TABS: readonly SubTab[] = ['activity', 'parents'];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BUCKET_LABELS: Record<GrantForm['bucket'], string> = {
  non_withdrawable: 'Non-Withdrawable (school grant)',
  withdrawable: 'Withdrawable (refundable)',
};

const TX_TYPE_LABELS: Record<BalanceTransaction['type'], string> = {
  credit: 'Credit',
  debit: 'Debit',
  refund: 'Refund',
  withdrawal: 'Withdrawal',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  admin_grant: 'Admin Grant',
  admin_correction: 'Admin Correction',
  stripe_payment: 'Stripe Payment',
  invoice: 'Invoice Payment',
  invoice_payment: 'Invoice Payment',
  purchase_bonus: 'Purchase Bonus',
  zelle_payment: 'Zelle Payment',
  payment_cart_funding: 'Bundle Payment Overpayment',
  enrollment_cancellation: 'Enrollment Cancellation',
  admin_refund_to_wallet: 'Refund to Wallet',
};

const ACTIVITY_WINDOW_LABELS: Record<ActivityWindow, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
};

const ACTIVITY_WINDOW_DAYS: Record<Exclude<ActivityWindow, 'all'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const SUB_TAB_LABELS: Record<SubTab, string> = {
  activity: 'Activity feed',
  parents: 'Per parent',
};

// The feed now owns a scrollable panel instead of sharing the page with the
// parent table, so it can show far more than the 12 rows that used to fit.
const RECENT_ACTIVITY_LIMIT = 100;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function parentName(first: string | null, last: string | null, email: string): string {
  const name = `${first ?? ''} ${last ?? ''}`.trim();
  return name || email;
}

function signedAmount(tx: BalanceTransaction): number {
  return tx.type === 'credit' || tx.type === 'refund' ? tx.amount_cents : -tx.amount_cents;
}

// ===========================================================================
// Component
// ===========================================================================

export function AdminBalances() {
  // ---- Balances list state ----
  const [balances, setBalances] = useState<BalanceSummaryRow[]>([]);
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [balancesError, setBalancesError] = useState<string | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('last_activity');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // ---- Overview state ----
  const [subTab, setSubTab] = useUrlTabState<SubTab>('tab', BALANCES_TABS, 'activity');
  const [activityWindow, setActivityWindow] = useState<ActivityWindow>('30d');
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [achInFlight, setAchInFlight] = useState<AchInFlightRow[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // ---- History modal state ----
  const [historyParent, setHistoryParent] = useState<BalanceSummaryRow | null>(null);
  const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [achPending, setAchPending] = useState<AchPendingRow[]>([]);

  // ---- Correction modal state ----
  const [correctionTx, setCorrectionTx] = useState<BalanceTransaction | null>(null);
  const [correctionNote, setCorrectionNote] = useState('');
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);

  // ---- Row selection state ----
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  // ---- Withdrawal modal state ----
  const [withdrawalParent, setWithdrawalParent] = useState<BalanceSummaryRow | null>(null);
  const [withdrawalForm, setWithdrawalForm] = useState<WithdrawalForm>({ amount: '', method: 'zelle', note: '' });
  const [withdrawalSubmitting, setWithdrawalSubmitting] = useState(false);
  const [withdrawalError, setWithdrawalError] = useState<string | null>(null);
  const [withdrawalSuccess, setWithdrawalSuccess] = useState<string | null>(null);

  // ---- Grant modal state ----
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<ParentResult | null>(null);
  const [grantForm, setGrantForm] = useState<GrantForm>({ amount: '', bucket: 'non_withdrawable', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [grantResult, setGrantResult] = useState<GrantResult | null>(null);

  // =========================================================================
  // Data fetching
  // =========================================================================

  const fetchBalances = useCallback(async () => {
    setBalancesLoading(true);
    setBalancesError(null);
    try {
      const { data, error } = await supabase
        .rpc('get_balance_account_summary');
      if (error) throw error;

      type RpcRow = Omit<BalanceSummaryRow, 'parent_first_name' | 'parent_last_name' | 'parent_email'>;
      const rpcRows = (data ?? []) as unknown as RpcRow[];

      // Fetch parent details for all accounts
      const parentIds = rpcRows.map((r) => r.parent_id).filter(Boolean);
      const parentsMap: Record<string, { first_name: string | null; last_name: string | null; email: string }> = {};
      if (parentIds.length > 0) {
        const { data: parents, error: pErr } = await supabase
          .from('parents')
          .select('id, first_name, last_name, email')
          .in('id', parentIds);
        if (pErr) throw pErr;
        for (const p of parents ?? []) {
          parentsMap[p.id] = p;
        }
      }

      const rows: BalanceSummaryRow[] = rpcRows.map((r) => {
        const p = parentsMap[r.parent_id];
        return {
          ...r,
          parent_first_name: p?.first_name ?? null,
          parent_last_name: p?.last_name ?? null,
          parent_email: p?.email ?? r.parent_id,
        };
      });

      setBalances(rows);
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Failed to load balances';
      setBalancesError(msg);
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  // Cross-parent activity + money still in flight. Admins have full RLS access to
  // both tables via the "Admins can do everything" policies.
  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      let txQuery = supabase
        .from('balance_transactions')
        .select('id, account_id, type, bucket, amount_cents, source_ref_type, note, created_at')
        .order('created_at', { ascending: false });

      if (activityWindow !== 'all') {
        const since = new Date();
        since.setDate(since.getDate() - ACTIVITY_WINDOW_DAYS[activityWindow]);
        txQuery = txQuery.gte('created_at', since.toISOString());
      }

      const [{ data: txData, error: txErr }, { data: achData, error: achErr }] = await Promise.all([
        txQuery,
        supabase
          .from('balance_ach_pending_purchases')
          .select('id, parent_id, amount_cents, created_at')
          .eq('status', 'processing')
          .order('created_at', { ascending: false }),
      ]);
      if (txErr) throw txErr;
      if (achErr) throw achErr;

      setActivity((txData ?? []) as ActivityRow[]);
      setAchInFlight((achData ?? []) as AchInFlightRow[]);
    } catch {
      setActivity([]);
      setAchInFlight([]);
    } finally {
      setActivityLoading(false);
    }
  }, [activityWindow]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  const fetchTransactions = useCallback(async (accountId: string) => {
    setTxLoading(true);
    try {
      const { data, error } = await supabase
        .from('balance_transactions')
        .select('id, account_id, type, bucket, amount_cents, source_ref_type, source_ref_id, related_tx_id, note, created_by, created_at')
        .eq('account_id', accountId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setTransactions(data ?? []);
    } catch {
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  }, []);

  // Pending/failed ACH Add Funds purchases for a parent — these have not yet
  // credited the wallet. Keyed on parent_id (not account_id).
  const fetchAchPending = useCallback(async (parentId: string) => {
    try {
      const { data, error } = await supabase
        .from('balance_ach_pending_purchases')
        .select('id, amount_cents, status, created_at')
        .eq('parent_id', parentId)
        .in('status', ['processing', 'failed'])
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAchPending((data ?? []) as AchPendingRow[]);
    } catch {
      setAchPending([]);
    }
  }, []);

  // =========================================================================
  // Balances list — sort & filter
  // =========================================================================

  const filteredBalances = balances.filter((b) => {
    if (!listSearch) return true;
    const term = listSearch.toLowerCase();
    const name = parentName(b.parent_first_name, b.parent_last_name, b.parent_email).toLowerCase();
    return name.includes(term) || b.parent_email.toLowerCase().includes(term);
  });

  const sortedBalances = [...filteredBalances].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'family': {
        const aName = parentName(a.parent_first_name, a.parent_last_name, a.parent_email);
        const bName = parentName(b.parent_first_name, b.parent_last_name, b.parent_email);
        return aName.localeCompare(bName) * dir;
      }
      case 'total':
        return (a.total_cents - b.total_cents) * dir;
      case 'withdrawable':
        return (a.withdrawable_cents - b.withdrawable_cents) * dir;
      case 'non_withdrawable':
        return (a.non_withdrawable_cents - b.non_withdrawable_cents) * dir;
      case 'last_activity': {
        const aTime = a.last_activity_at ?? '';
        const bTime = b.last_activity_at ?? '';
        return aTime.localeCompare(bTime) * dir;
      }
      default:
        return 0;
    }
  });

  // Aggregate totals
  const aggTotal = filteredBalances.reduce((s, b) => s + b.total_cents, 0);
  const aggWithdrawable = filteredBalances.reduce((s, b) => s + b.withdrawable_cents, 0);
  const aggNonWithdrawable = filteredBalances.reduce((s, b) => s + b.non_withdrawable_cents, 0);

  // account_id → parent, so the cross-parent feed can name each row. Every account
  // with a transaction appears in the summary RPC, so this covers the feed.
  const accountParent = useMemo(() => {
    const map: Record<string, BalanceSummaryRow> = {};
    for (const b of balances) map[b.account_id] = b;
    return map;
  }, [balances]);

  const parentById = useMemo(() => {
    const map: Record<string, BalanceSummaryRow> = {};
    for (const b of balances) map[b.parent_id] = b;
    return map;
  }, [balances]);

  // Flow over the selected window. Distinct from the liability tiles above, which
  // are a point-in-time snapshot with no time dimension.
  const activityTotals = useMemo(() => summarizeCashActivity(activity), [activity]);

  const achInFlightCents = achInFlight.reduce((s, r) => s + r.amount_cents, 0);
  const staleAchCount = countStaleAch(achInFlight);

  const recentActivity = activity.slice(0, RECENT_ACTIVITY_LIMIT);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }

  function SortIcon({ field }: { field: SortField }) {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-gray-400 inline ml-1" />;
    return <span className="text-indigo-600 ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  }

  // =========================================================================
  // History modal
  // =========================================================================

  function openHistory(row: BalanceSummaryRow) {
    setHistoryParent(row);
    fetchTransactions(row.account_id);
    fetchAchPending(row.parent_id);
  }

  function closeHistory() {
    setHistoryParent(null);
    setTransactions([]);
    setAchPending([]);
  }

  // Running balance computation
  // txs arrives newest-first; compute on oldest-first then reverse to match display order.
  function computeRunningBalances(txs: BalanceTransaction[]): number[] {
    let running = 0;
    const ascending = [...txs].reverse();
    const balances = ascending.map((tx) => {
      running += signedAmount(tx);
      return running;
    });
    return balances.reverse();
  }

  // CSV export
  function handleExportCSV() {
    if (!historyParent || transactions.length === 0) return;
    const running = computeRunningBalances(transactions);
    const headers = ['Date', 'Type', 'Bucket', 'Amount', 'Source', 'Note', 'Running Balance'];
    const rows = transactions.map((tx, i) => [
      `"${formatDateTimeInBusinessTZ(tx.created_at, { hour12: true })}"`,
      `"${TX_TYPE_LABELS[tx.type]}"`,
      `"${tx.bucket === 'withdrawable' ? 'Withdrawable' : 'Non-Withdrawable'}"`,
      `"${formatCents(tx.amount_cents)}"`,
      `"${tx.source_ref_type ? (SOURCE_TYPE_LABELS[tx.source_ref_type] ?? tx.source_ref_type) : ''}"`,
      `"${(tx.note ?? '').replace(/"/g, '""')}"`,
      `"${formatCents(running[i])}"`,
    ].join(','));

    const name = parentName(historyParent.parent_first_name, historyParent.parent_last_name, historyParent.parent_email);
    const csvContent = [headers.join(','), ...rows].join('\n');
    downloadCSV(csvContent, `balance_history_${name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
  }

  // =========================================================================
  // Process Withdrawal
  // =========================================================================

  function openWithdrawal(row: BalanceSummaryRow) {
    setWithdrawalParent(row);
    setWithdrawalForm({ amount: '', method: 'zelle', note: '' });
    setWithdrawalError(null);
    setWithdrawalSuccess(null);
  }

  function closeWithdrawal() {
    setWithdrawalParent(null);
    setWithdrawalError(null);
    setWithdrawalSuccess(null);
  }

  async function handleWithdrawalSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!withdrawalParent) return;

    const amountCents = Math.round(parseFloat(withdrawalForm.amount) * 100);
    if (!withdrawalForm.amount || isNaN(amountCents) || amountCents <= 0) {
      setWithdrawalError('Please enter a valid amount greater than $0.');
      return;
    }
    if (amountCents > withdrawalParent.withdrawable_cents) {
      setWithdrawalError(`Amount exceeds withdrawable balance of ${formatCents(withdrawalParent.withdrawable_cents)}.`);
      return;
    }
    if (!withdrawalForm.note.trim()) {
      setWithdrawalError('A note is required.');
      return;
    }

    setWithdrawalSubmitting(true);
    setWithdrawalError(null);
    setWithdrawalSuccess(null);

    try {
      const { data, error } = await supabase.functions.invoke('balance-process-withdrawal', {
        body: {
          parent_id: withdrawalParent.parent_id,
          amount_cents: amountCents,
          method: withdrawalForm.method,
          note: withdrawalForm.note.trim(),
        },
      });

      if (error) {
        const msg = (error as { message?: string }).message ?? 'Unknown error';
        setWithdrawalError(`Withdrawal failed: ${msg}`);
        return;
      }

      const result = data as {
        status: string;
        new_total_balance_cents: number;
        new_withdrawable_balance_cents: number;
        new_non_withdrawable_balance_cents: number;
      };

      setWithdrawalSuccess(
        `Withdrawal processed! New balance: ${formatCents(result.new_total_balance_cents)} (Withdrawable: ${formatCents(result.new_withdrawable_balance_cents)})`
      );
      setWithdrawalForm({ amount: '', method: 'zelle', note: '' });

      // Refresh balances list
      fetchBalances();
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Unexpected error';
      setWithdrawalError(`Withdrawal failed: ${msg}`);
    } finally {
      setWithdrawalSubmitting(false);
    }
  }

  // =========================================================================
  // Grant modal handlers
  // =========================================================================

  function openGrantModal(row: BalanceSummaryRow) {
    setSelectedParent({
      id: row.parent_id,
      first_name: row.parent_first_name,
      last_name: row.parent_last_name,
      email: row.parent_email,
    });
    setGrantForm({ amount: '', bucket: 'non_withdrawable', note: '' });
    setGrantResult(null);
    setSubmitError(null);
    setGrantModalOpen(true);
  }

  function closeGrantModal() {
    setGrantModalOpen(false);
    setSelectedParent(null);
    setGrantResult(null);
    setSubmitError(null);
  }

  async function handleGrantSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedParent) return;

    const amountCents = Math.round(parseFloat(grantForm.amount) * 100);
    if (!grantForm.amount || isNaN(amountCents) || amountCents <= 0) {
      setSubmitError('Please enter a valid amount greater than $0.');
      return;
    }
    if (!grantForm.note.trim()) {
      setSubmitError('A note is required.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setGrantResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('balance-admin-grant', {
        body: {
          parent_id: selectedParent.id,
          amount_cents: amountCents,
          bucket: grantForm.bucket,
          note: grantForm.note.trim(),
        },
      });

      if (error) {
        const msg = (error as { message?: string }).message ?? 'Unknown error';
        setSubmitError(`Grant failed: ${msg}`);
        return;
      }

      const result = data as GrantResult;
      setGrantResult(result);
      setGrantForm({ amount: '', bucket: 'non_withdrawable', note: '' });
      fetchBalances();
      // Keep modal open to show success result; user can close manually
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Unexpected error';
      setSubmitError(`Grant failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  const parentDisplayName = selectedParent
    ? `${selectedParent.first_name ?? ''} ${selectedParent.last_name ?? ''}`.trim() || selectedParent.email
    : null;

  const runningBalances = computeRunningBalances(transactions);

  // Set of original tx IDs that already have a correction debit applied
  const reversedTxIds = useMemo(() => {
    const ids = new Set<string>();
    for (const tx of transactions) {
      if (tx.source_ref_type === 'admin_correction' && tx.source_ref_id) {
        ids.add(tx.source_ref_id);
      }
    }
    return ids;
  }, [transactions]);

  // =========================================================================
  // Admin Correction
  // =========================================================================

  function openCorrection(tx: BalanceTransaction) {
    setCorrectionTx(tx);
    setCorrectionNote('');
    setCorrectionError(null);
  }

  function closeCorrection() {
    setCorrectionTx(null);
    setCorrectionError(null);
  }

  async function handleCorrectionSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!correctionTx || !historyParent) return;

    const note = correctionNote.trim() || `Admin correction: reversed grant of ${formatCents(correctionTx.amount_cents)}`;

    setCorrectionSubmitting(true);
    setCorrectionError(null);

    try {
      const { data, error } = await supabase.functions.invoke('balance-admin-correction', {
        body: { original_tx_id: correctionTx.id, note },
      });

      if (error) {
        setCorrectionError((error as { message?: string }).message ?? 'Unknown error');
        return;
      }

      const result = data as { new_total_balance_cents: number; new_withdrawable_balance_cents: number; new_non_withdrawable_balance_cents: number };
      setHistoryParent((prev) =>
        prev
          ? {
              ...prev,
              total_cents: result.new_total_balance_cents,
              withdrawable_cents: result.new_withdrawable_balance_cents,
              non_withdrawable_cents: result.new_non_withdrawable_balance_cents,
            }
          : prev
      );
      fetchTransactions(historyParent.account_id);
      fetchBalances();
      closeCorrection();
    } catch (err: unknown) {
      setCorrectionError((err as { message?: string }).message ?? 'Unexpected error');
    } finally {
      setCorrectionSubmitting(false);
    }
  }

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-4 md:flex md:flex-col md:flex-1 md:min-h-0">
        <div className="bg-white shadow rounded-lg overflow-hidden md:flex md:flex-col md:flex-1 md:min-h-0">
            {/* Always-visible header: flow over the selected window. Sits above the
                sub-tabs so the headline numbers stay on screen whichever tab is open. */}
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Activity</h2>
                  {/* BHCS Cash is family-level by decision (one family, one wallet), so these
                      figures cannot be split by brand -- funding is not for any program. Said here
                      so they are not read as BHCS-only. */}
                  <p className="text-xs text-gray-500">
                    One wallet per family, shared by BHCS and Kaleido. Every figure here covers both brands.
                  </p>
                </div>
                <div className="flex rounded-md border border-gray-300 overflow-hidden">
                  {(Object.keys(ACTIVITY_WINDOW_LABELS) as ActivityWindow[]).map((w) => (
                    <button
                      key={w}
                      onClick={() => setActivityWindow(w)}
                      className={`px-2.5 py-1 text-xs font-medium border-r border-gray-300 last:border-r-0 transition-colors ${
                        activityWindow === w ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {ACTIVITY_WINDOW_LABELS[w]}
                    </button>
                  ))}
                </div>
              </div>

              {activityLoading ? (
                <p className="text-sm text-gray-500 py-4">Loading activity...</p>
              ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <div className="bg-green-50 border border-green-100 rounded-md px-3 py-2">
                      <p className="text-xs font-medium text-green-800 uppercase">Funded</p>
                      <p className="text-base font-bold text-green-900">{formatCents(activityTotals.funded)}</p>
                      <p className="text-xs text-green-700">card, ACH &amp; Zelle</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
                      <p className="text-xs font-medium text-blue-800 uppercase">Spent</p>
                      <p className="text-base font-bold text-blue-900">{formatCents(activityTotals.spent)}</p>
                      <p className="text-xs text-blue-700">applied to invoices</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                      <p className="text-xs font-medium text-amber-800 uppercase">Granted</p>
                      <p className="text-base font-bold text-amber-900">{formatCents(activityTotals.granted)}</p>
                      <p className="text-xs text-amber-700">
                        {activityTotals.bonus > 0 ? `+ ${formatCents(activityTotals.bonus)} bonus` : 'admin grants'}
                      </p>
                    </div>
                    <div className="bg-orange-50 border border-orange-100 rounded-md px-3 py-2">
                      <p className="text-xs font-medium text-orange-800 uppercase">Withdrawn</p>
                      <p className="text-base font-bold text-orange-900">{formatCents(activityTotals.withdrawn)}</p>
                      <p className="text-xs text-orange-700">cashed out</p>
                    </div>
                    <div
                      className={`rounded-md px-3 py-2 border ${
                        staleAchCount > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <p className={`text-xs font-medium uppercase ${staleAchCount > 0 ? 'text-red-800' : 'text-gray-600'}`}>
                        ACH in flight
                      </p>
                      <p className={`text-base font-bold ${staleAchCount > 0 ? 'text-red-900' : 'text-gray-900'}`}>
                        {formatCents(achInFlightCents)}
                      </p>
                      <p className={`text-xs ${staleAchCount > 0 ? 'text-red-700' : 'text-gray-500'}`}>
                        {achInFlight.length === 0
                          ? 'nothing pending'
                          : staleAchCount > 0
                            ? `${staleAchCount} over ${ACH_STALE_DAYS}d`
                            : `${achInFlight.length} settling`}
                      </p>
                    </div>
                  </div>
              )}
            </div>

            {/* Sub-tabs. The cross-parent feed and the per-parent roster are both
                long lists; stacked, the feed crowded the table down to a single
                visible row on desktop. */}
            <div className="px-4 sm:px-6 flex gap-6 border-b border-gray-200 flex-shrink-0">
              {(Object.keys(SUB_TAB_LABELS) as SubTab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setSubTab(t)}
                  aria-current={subTab === t ? 'page' : undefined}
                  className={`-mb-px py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    subTab === t
                      ? 'border-indigo-600 text-indigo-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {SUB_TAB_LABELS[t]}
                  <span className="ml-1.5 text-xs font-normal text-gray-400">
                    {t === 'activity' ? activity.length : filteredBalances.length}
                  </span>
                </button>
              ))}
            </div>

            {/* ================= TAB: Recent activity ================= */}
            {subTab === 'activity' && (
              <div className="px-4 sm:px-6 py-4 overflow-y-auto md:flex-1 md:min-h-0">
                {activityLoading ? (
                  <p className="text-sm text-gray-500 py-4">Loading activity...</p>
                ) : (
                  <>
                  {/* Purchases that have left the parent's bank but not yet landed.
                      ACH takes 4-5 days, so this is the only place the money exists. */}
                  {achInFlight.length > 0 && (
                    <div className="mb-4 border border-gray-200 rounded-md overflow-hidden">
                      <p className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                        Pending ACH top-ups
                      </p>
                      <ul className="divide-y divide-gray-100">
                        {achInFlight.map((row) => {
                          const p = parentById[row.parent_id];
                          const ageDays = achAgeDays(row.created_at);
                          const stale = ageDays > ACH_STALE_DAYS;
                          return (
                            <li key={row.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                              <span className="min-w-0 truncate text-gray-900">
                                {p ? parentName(p.parent_first_name, p.parent_last_name, p.parent_email) : row.parent_id}
                              </span>
                              <span className="flex items-center gap-3 flex-shrink-0">
                                <span className={`text-xs ${stale ? 'text-red-700 font-medium' : 'text-gray-500'}`}>
                                  started {formatDateTimeInBusinessTZ(row.created_at)} · {ageDays}d ago
                                </span>
                                <span className="font-semibold text-gray-900">{formatCents(row.amount_cents)}</span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Recent activity across all parents */}
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <p className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-700 uppercase border-b border-gray-200">
                      Recent activity
                    </p>
                    {recentActivity.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-gray-500">
                        No BHCS Cash activity in this period.
                      </p>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {recentActivity.map((tx) => {
                          const p = accountParent[tx.account_id];
                          const isCredit = tx.type === 'credit' || tx.type === 'refund';
                          return (
                            <li key={tx.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                              <span className="min-w-0 flex-1">
                                <span className="text-gray-900 truncate">
                                  {p ? parentName(p.parent_first_name, p.parent_last_name, p.parent_email) : '—'}
                                </span>
                                <span className="text-gray-500">
                                  {' · '}
                                  {tx.source_ref_type
                                    ? SOURCE_TYPE_LABELS[tx.source_ref_type] ?? tx.source_ref_type
                                    : TX_TYPE_LABELS[tx.type]}
                                </span>
                              </span>
                              <span className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-xs text-gray-500 hidden sm:inline">
                                  {formatDateTimeInBusinessTZ(tx.created_at)}
                                </span>
                                <span className={`font-semibold ${isCredit ? 'text-green-700' : 'text-gray-900'}`}>
                                  {isCredit ? '+' : '−'}{formatCents(tx.amount_cents)}
                                </span>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {activity.length > recentActivity.length && (
                      <p className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100">
                        Showing the {recentActivity.length} most recent of {activity.length} transactions in this
                        period.
                      </p>
                    )}
                  </div>
                  </>
                )}
              </div>
            )}

            {/* ================= TAB: Per parent ================= */}
            {subTab === 'parents' && (
              <>
              {/* Aggregate totals */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex flex-wrap items-center gap-6">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Total Liability</p>
                    <p className="text-lg font-bold text-gray-900">{formatCents(aggTotal)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Withdrawable</p>
                    <p className="text-lg font-semibold text-green-700">{formatCents(aggWithdrawable)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Non-Withdrawable</p>
                    <p className="text-lg font-semibold text-amber-700">{formatCents(aggNonWithdrawable)}</p>
                  </div>
                  <div className="ml-auto text-sm text-gray-500">
                    {filteredBalances.length} {filteredBalances.length === 1 ? 'parent' : 'parents'}
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className="px-4 sm:px-6 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder="Filter by name or email..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              {/* Table */}
              {balancesLoading ? (
                <div className="px-6 py-12 text-center text-gray-500 text-sm">Loading balances...</div>
              ) : balancesError ? (
                <div className="px-6 py-12 text-center text-red-600 text-sm">{balancesError}</div>
              ) : sortedBalances.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500 text-sm">
                  {listSearch ? 'No parents match your search.' : 'No parents with balances yet.'}
                </div>
              ) : (
                <>
                  {/* Mobile card list */}
                  <div className="sm:hidden divide-y divide-gray-200">
                    {sortedBalances.map((row) => (
                      <div
                        key={row.account_id}
                        className={`px-4 py-3 cursor-pointer ${selectedAccountId === row.account_id ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedAccountId(selectedAccountId === row.account_id ? null : row.account_id)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {parentName(row.parent_first_name, row.parent_last_name, row.parent_email)}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{row.parent_email}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-gray-900">{formatCents(row.total_cents)}</p>
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                                row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {row.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 flex gap-3 text-xs">
                          <span className="text-green-700">W: {formatCents(row.withdrawable_cents)}</span>
                          <span className="text-amber-700">NW: {formatCents(row.non_withdrawable_cents)}</span>
                        </div>
                        {selectedAccountId === row.account_id && (
                          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-blue-100">
                            <button
                              onClick={(e) => { e.stopPropagation(); openHistory(row); }}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 hover:text-indigo-900 transition-colors"
                            >
                              History
                            </button>
                            {row.withdrawable_cents > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openWithdrawal(row); }}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors"
                              >
                                Withdraw
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); openGrantModal(row); }}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                            >
                              Grant
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden sm:block overflow-x-auto overflow-y-auto md:flex-1 md:min-h-0">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('family')}
                          >
                            Parent <SortIcon field="family" />
                          </th>
                          <th
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('total')}
                          >
                            Total <SortIcon field="total" />
                          </th>
                          <th
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('withdrawable')}
                          >
                            Withdrawable <SortIcon field="withdrawable" />
                          </th>
                          <th
                            className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('non_withdrawable')}
                          >
                            Non-Withdrawable <SortIcon field="non_withdrawable" />
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('last_activity')}
                          >
                            Last Activity <SortIcon field="last_activity" />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sortedBalances.map((row) => (
                          <React.Fragment key={row.account_id}>
                          <tr
                            className={selectedAccountId === row.account_id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                            onClick={() => setSelectedAccountId(selectedAccountId === row.account_id ? null : row.account_id)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <p className="text-sm font-medium text-gray-900">
                                {parentName(row.parent_first_name, row.parent_last_name, row.parent_email)}
                              </p>
                              <p className="text-xs text-gray-500">{row.parent_email}</p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                              {formatCents(row.total_cents)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-green-700">
                              {formatCents(row.withdrawable_cents)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-amber-700">
                              {formatCents(row.non_withdrawable_cents)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {row.last_activity_at
                                ? formatDateTimeInBusinessTZ(row.last_activity_at, { hour12: true })
                                : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  row.is_active
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {row.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                          {selectedAccountId === row.account_id && (
                            <tr className="bg-blue-50">
                              <td colSpan={6} className="px-6 py-2 border-b border-blue-100">
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openHistory(row); }}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 hover:text-indigo-900 transition-colors"
                                  >
                                    History
                                  </button>
                                  {row.withdrawable_cents > 0 && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openWithdrawal(row); }}
                                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100 hover:text-orange-900 transition-colors"
                                    >
                                      Withdraw
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openGrantModal(row); }}
                                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 hover:text-green-900 transition-colors"
                                  >
                                    Grant
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              </>
            )}
          </div>
        </div>

      {/* ================================================================= */}
      {/* MODAL: Grant Balance */}
      {/* ================================================================= */}
      <Modal
        isOpen={grantModalOpen}
        onClose={closeGrantModal}
        title={`Grant Balance — ${selectedParent ? parentDisplayName : ''}`}
      >
        {selectedParent && (
          <div>
            <div className="mb-4 p-3 bg-indigo-50 rounded-md text-sm text-indigo-800">
              <span className="font-semibold">{parentDisplayName}</span>
              <span className="text-indigo-500 ml-2">({selectedParent.email})</span>
            </div>

            {grantResult ? (
              <div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm font-semibold text-green-800 mb-2">Balance granted successfully!</p>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <dt className="text-gray-500">New Total Balance</dt>
                    <dd className="text-gray-900 font-medium">{formatCents(grantResult.new_total_balance_cents)}</dd>
                    <dt className="text-gray-500">Withdrawable</dt>
                    <dd className="text-gray-900">{formatCents(grantResult.new_withdrawable_balance_cents)}</dd>
                    <dt className="text-gray-500">Non-Withdrawable</dt>
                    <dd className="text-gray-900">{formatCents(grantResult.new_non_withdrawable_balance_cents)}</dd>
                  </dl>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={closeGrantModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleGrantSubmit} noValidate>
                <div className="space-y-4">
                  {/* Amount */}
                  <div>
                    <label htmlFor="grant-amount" className="block text-sm font-medium text-gray-700 mb-1">
                      Amount (USD) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-400 text-sm">$</span>
                      </div>
                      <input
                        id="grant-amount"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={grantForm.amount}
                        onChange={(e) => setGrantForm((f) => ({ ...f, amount: e.target.value }))}
                        placeholder="0.00"
                        className="block w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>

                  {/* Bucket */}
                  <div>
                    <label htmlFor="grant-bucket" className="block text-sm font-medium text-gray-700 mb-1">
                      Bucket
                    </label>
                    <select
                      id="grant-bucket"
                      value={grantForm.bucket}
                      onChange={(e) =>
                        setGrantForm((f) => ({ ...f, bucket: e.target.value as GrantForm['bucket'] }))
                      }
                      className="block w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {(Object.keys(BUCKET_LABELS) as GrantForm['bucket'][]).map((b) => (
                        <option key={b} value={b}>
                          {BUCKET_LABELS[b]}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Non-withdrawable credit can only be used toward invoices and cannot be refunded as cash.
                    </p>
                  </div>

                  {/* Note */}
                  <div>
                    <label htmlFor="grant-note" className="block text-sm font-medium text-gray-700 mb-1">
                      Note <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="grant-note"
                      value={grantForm.note}
                      onChange={(e) => setGrantForm((f) => ({ ...f, note: e.target.value }))}
                      rows={3}
                      placeholder="Reason for grant (required)"
                      className="block w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {submitError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeGrantModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !grantForm.amount || !grantForm.note.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Granting...' : 'Grant Balance'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </Modal>

      {/* ================================================================= */}
      {/* MODAL: Family Transaction History */}
      {/* ================================================================= */}
      <Modal
        isOpen={historyParent !== null}
        onClose={closeHistory}
        title={`Transaction History — ${historyParent ? parentName(historyParent.parent_first_name, historyParent.parent_last_name, historyParent.parent_email) : ''}`}
      >
        {historyParent && (
          <div>
            {/* Balance summary */}
            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <div className="flex flex-wrap gap-4">
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-sm font-bold">{formatCents(historyParent.total_cents)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Withdrawable</p>
                  <p className="text-sm font-semibold text-green-700">{formatCents(historyParent.withdrawable_cents)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Non-Withdrawable</p>
                  <p className="text-sm font-semibold text-amber-700">{formatCents(historyParent.non_withdrawable_cents)}</p>
                </div>
              </div>
              {transactions.length > 0 && (
                <div className="mt-3">
                  <button
                    onClick={handleExportCSV}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Download className="h-3 w-3" />
                    Export CSV
                  </button>
                </div>
              )}
            </div>

            {/* Pending / failed ACH Add Funds — not yet credited to the wallet */}
            {achPending.length > 0 && (
              <div className="mb-4 space-y-2">
                {achPending.map((row) => (
                  row.status === 'processing' ? (
                    <div
                      key={row.id}
                      className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800"
                      data-testid="admin-ach-processing"
                    >
                      ACH Add Funds of {formatCents(row.amount_cents)} submitted on{' '}
                      {formatDateTimeInBusinessTZ(row.created_at, { hour12: true })} is{' '}
                      <span className="font-semibold">processing</span> — not yet credited.
                      Funds post once the bank transfer settles (3–5 business days).
                    </div>
                  ) : (
                    <div
                      key={row.id}
                      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                      data-testid="admin-ach-failed"
                    >
                      ACH Add Funds of {formatCents(row.amount_cents)} submitted on{' '}
                      {formatDateTimeInBusinessTZ(row.created_at, { hour12: true })}{' '}
                      <span className="font-semibold">failed</span> — no funds were credited.
                    </div>
                  )
                ))}
              </div>
            )}

            {/* Transactions table */}
            {txLoading ? (
              <div className="py-8 text-center text-gray-500 text-sm">Loading transactions...</div>
            ) : transactions.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">No transactions found.</div>
            ) : (
              <>
                {/* Mobile card list */}
                <div className="sm:hidden divide-y divide-gray-200 max-h-96 overflow-y-auto">
                  {transactions.map((tx, i) => {
                    const isPositive = tx.type === 'credit' || tx.type === 'refund';
                    const canReverse = tx.type === 'credit' && tx.source_ref_type === 'admin_grant' && !reversedTxIds.has(tx.id);
                    const isReversed = reversedTxIds.has(tx.id);
                    return (
                      <div key={tx.id} className="py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-500">
                              {formatDateTimeInBusinessTZ(tx.created_at, { hour12: true })}
                            </p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {isPositive ? (
                                <Plus className="h-3 w-3 text-green-600 flex-shrink-0" />
                              ) : (
                                <Minus className="h-3 w-3 text-red-500 flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium text-gray-900">
                                {TX_TYPE_LABELS[tx.type]}
                              </span>
                              <span className="text-xs text-gray-400">
                                · {tx.bucket === 'withdrawable' ? 'W' : 'NW'}
                              </span>
                            </div>
                            {tx.source_ref_type && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {SOURCE_TYPE_LABELS[tx.source_ref_type] ?? tx.source_ref_type}
                              </p>
                            )}
                            {tx.note && (
                              <p className="text-xs text-gray-400 mt-0.5 truncate" title={tx.note}>
                                {tx.note}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`text-sm font-semibold ${isPositive ? 'text-green-700' : 'text-red-600'}`}>
                              {isPositive ? '+' : '-'}{formatCents(tx.amount_cents)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Bal: {formatCents(runningBalances[i])}
                            </p>
                          </div>
                        </div>
                        {(canReverse || isReversed) && (
                          <div className="mt-1.5">
                            {canReverse && (
                              <button
                                onClick={() => openCorrection(tx)}
                                className="text-xs text-red-600 hover:text-red-800 font-medium"
                              >
                                Reverse
                              </button>
                            )}
                            {isReversed && (
                              <span className="text-xs text-gray-400 italic">Reversed</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bucket</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                        <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {transactions.map((tx, i) => {
                        const isPositive = tx.type === 'credit' || tx.type === 'refund';
                        const canReverse = tx.type === 'credit' && tx.source_ref_type === 'admin_grant' && !reversedTxIds.has(tx.id);
                        const isReversed = reversedTxIds.has(tx.id);
                        return (
                          <tr key={tx.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {formatDateTimeInBusinessTZ(tx.created_at, { hour12: true })}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1">
                                {isPositive ? (
                                  <Plus className="h-3 w-3 text-green-600" />
                                ) : (
                                  <Minus className="h-3 w-3 text-red-500" />
                                )}
                                {TX_TYPE_LABELS[tx.type]}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {tx.bucket === 'withdrawable' ? 'W' : 'NW'}
                            </td>
                            <td className={`px-3 py-2 whitespace-nowrap text-right font-medium ${isPositive ? 'text-green-700' : 'text-red-600'}`}>
                              {isPositive ? '+' : '-'}{formatCents(tx.amount_cents)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                              {tx.source_ref_type
                                ? (SOURCE_TYPE_LABELS[tx.source_ref_type] ?? tx.source_ref_type)
                                : '-'}
                            </td>
                            <td className="px-3 py-2 max-w-[200px] truncate text-gray-500" title={tx.note ?? undefined}>
                              {tx.note || '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right font-semibold text-gray-900">
                              {formatCents(runningBalances[i])}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-right">
                              {canReverse && (
                                <button
                                  onClick={() => openCorrection(tx)}
                                  className="text-xs text-red-600 hover:text-red-800 font-medium"
                                >
                                  Reverse
                                </button>
                              )}
                              {isReversed && (
                                <span className="text-xs text-gray-400 italic">Reversed</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ================================================================= */}
      {/* MODAL: Process Withdrawal */}
      {/* ================================================================= */}
      <Modal
        isOpen={withdrawalParent !== null}
        onClose={closeWithdrawal}
        title={`Process Withdrawal — ${withdrawalParent ? parentName(withdrawalParent.parent_first_name, withdrawalParent.parent_last_name, withdrawalParent.parent_email) : ''}`}
      >
        {withdrawalParent && (
          <div>
            <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm">
              <p>
                Withdrawable balance:{' '}
                <span className="font-bold text-green-700">{formatCents(withdrawalParent.withdrawable_cents)}</span>
              </p>
            </div>

            <form onSubmit={handleWithdrawalSubmit} noValidate>
              <div className="space-y-4">
                {/* Amount */}
                <div>
                  <label htmlFor="withdrawal-amount" className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (USD) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-400 text-sm">$</span>
                    </div>
                    <input
                      id="withdrawal-amount"
                      type="number"
                      min="0.01"
                      step="0.01"
                      max={(withdrawalParent.withdrawable_cents / 100).toFixed(2)}
                      value={withdrawalForm.amount}
                      onChange={(e) => setWithdrawalForm((f) => ({ ...f, amount: e.target.value }))}
                      placeholder="0.00"
                      className="block w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                {/* Method */}
                <div>
                  <label htmlFor="withdrawal-method" className="block text-sm font-medium text-gray-700 mb-1">
                    Method
                  </label>
                  <select
                    id="withdrawal-method"
                    value={withdrawalForm.method}
                    onChange={(e) =>
                      setWithdrawalForm((f) => ({ ...f, method: e.target.value as WithdrawalForm['method'] }))
                    }
                    className="block w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="zelle">Zelle</option>
                    <option value="cash">Cash / Check</option>
                  </select>
                </div>

                {/* Note */}
                <div>
                  <label htmlFor="withdrawal-note" className="block text-sm font-medium text-gray-700 mb-1">
                    Note <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="withdrawal-note"
                    value={withdrawalForm.note}
                    onChange={(e) => setWithdrawalForm((f) => ({ ...f, note: e.target.value }))}
                    rows={2}
                    placeholder="Reason for withdrawal (required)"
                    className="block w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              {withdrawalError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {withdrawalError}
                </div>
              )}

              {withdrawalSuccess && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                  {withdrawalSuccess}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-3">
                {withdrawalSuccess ? (
                  <button
                    type="button"
                    onClick={closeWithdrawal}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={closeWithdrawal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={withdrawalSubmitting || !withdrawalForm.amount || !withdrawalForm.note.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {withdrawalSubmitting ? 'Processing...' : 'Process Withdrawal'}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        )}
      </Modal>

      {/* ================================================================= */}
      {/* MODAL: Admin Correction (reverse a grant) */}
      {/* ================================================================= */}
      <Modal
        isOpen={correctionTx !== null}
        onClose={closeCorrection}
        title="Reverse Grant"
      >
        {correctionTx && (
          <div>
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
              <p className="font-medium text-amber-900 mb-1">Transaction to reverse</p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <dt className="text-amber-700">Amount</dt>
                <dd className="text-amber-900 font-semibold">{formatCents(correctionTx.amount_cents)}</dd>
                <dt className="text-amber-700">Bucket</dt>
                <dd className="text-amber-900">{correctionTx.bucket === 'withdrawable' ? 'Withdrawable' : 'Non-Withdrawable'}</dd>
                <dt className="text-amber-700">Note</dt>
                <dd className="text-amber-900 truncate" title={correctionTx.note ?? undefined}>{correctionTx.note || '—'}</dd>
                <dt className="text-amber-700">Date</dt>
                <dd className="text-amber-900">{formatDateTimeInBusinessTZ(correctionTx.created_at, { hour12: true })}</dd>
              </dl>
            </div>

            <form onSubmit={handleCorrectionSubmit} noValidate>
              <div>
                <label htmlFor="correction-note" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for reversal
                </label>
                <textarea
                  id="correction-note"
                  value={correctionNote}
                  onChange={(e) => setCorrectionNote(e.target.value)}
                  rows={2}
                  placeholder={`Admin correction: reversed grant of ${formatCents(correctionTx.amount_cents)}`}
                  className="block w-full py-2 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <p className="mt-1 text-xs text-gray-400">Leave blank to use the default reason shown above.</p>
              </div>

              {correctionError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {correctionError}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeCorrection}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={correctionSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {correctionSubmitting ? 'Reversing...' : `Reverse ${formatCents(correctionTx.amount_cents)}`}
                </button>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}
